<script setup lang="ts">
  import type { SwiperSettings } from '@/components/ui/slider/types'
  import '@/assets/css/swiper.css'
  import type { IBestBuyBanner } from '@/types/BestBuyBanners'
  import type Swiper from 'swiper'
  import useIndexPageDataLayer from '@/composables/data-layers/useIndexPageDataLayer'

  defineProps<{ slides: IBestBuyBanner[]; darkTheme?: boolean }>()

  const { t } = useI18n()

  const sliderSettings: SwiperSettings = {
    speed: 500,
    spaceBetween: '24px',
    slidesPerView: 4,
    slidesPerGroup: 4,
    navigation: {
      nextEl: '#bbb-next',
      prevEl: '#bbb-prev'
    },
    pagination: {
      el: '#bbb-pagination',
      clickable: true,
      bulletActiveClass: 'border-bt-blue-dark rounded-full',
      renderBullet: (index: number, className: string) => {
        return `<span class="${className} border-b w-8 lg:w-14 border-b-4 border-border block h-1 cursor-pointer" role="button" aria-label="${t('slider.goToSlide', { index: index + 1 })}"></span>`
      }
    },
    keyboard: true,
    watchSlidesProgress: true,
    a11y: {
      containerMessage: t('aria.label.bestBuyBannerSlider'),
      paginationBulletMessage: t('slider.goToSlideFull', {
        index: '{{index}}',
        total: '{{slidesLength}}'
      })
    }
  }

  const activeIndex = ref(0)
  function onActiveIndexChange(event: CustomEvent<Swiper[]>) {
    activeIndex.value = event.detail[0].realIndex
  }

  const { pushBestBuyBannerNavigationDataLayer } = useIndexPageDataLayer()
  const onPrevClick = () => {
    pushBestBuyBannerNavigationDataLayer({
      event_label: 'back'
    })
  }

  const onNextClick = () => {
    pushBestBuyBannerNavigationDataLayer({
      event_label: 'forward'
    })
  }
</script>
<template>
  <section class="relative -mx-2">
    <LazyUiSliderComponent
      :slider-settings="sliderSettings"
      :slides="slides"
      @swiperactiveindexchange="onActiveIndexChange"
    >
      <template #slide="{ slide, index }">
        <BestBuyBannerCard
          :key="`${slide.origin}-${slide.dest}`"
          :data="slide"
          class="pb-4"
          :class="[{ 'ml-6': activeIndex === index }, { 'mr-6': activeIndex + 3 === index }]"
        />
      </template>
      <template #slider-container-end>
        <div
          id="bbb-pagination"
          class="mt-8 hidden justify-center gap-4 pb-4 sm:flex lg:mt-12"
        />
      </template>
      <template #slider-outside>
        <UiSliderNavigationRoundArrows
          class="bbb-navigation-arrows"
          prev-btn-id="bbb-prev"
          next-btn-id="bbb-next"
          :dark-theme="darkTheme"
          :hide-from-screen-readers="true"
          @prev-click="onPrevClick"
          @next-click="onNextClick"
        />
      </template>
    </LazyUiSliderComponent>
  </section>
</template>
<style lang="scss">
  .bbb-navigation-arrows {
    @apply absolute -left-12 top-[calc(50%-4rem)] hidden w-[calc(100%+6rem)] justify-between;

    @media (min-width: 1230px) and (max-width: 1419px) {
      display: flex;
    }

    @media (min-width: 1530px) {
      display: flex;
    }
  }
</style>
